import { lazy, Suspense } from 'react';
import { productRoutes } from './productRoutes';
import { firstMvp } from '@/lib/productScope';
import { createBrowserRouter } from 'react-router-dom';
import RequireMember from './guard/RequireMember';
import AppShell from './layouts/AppShell';
import FullScreenLayout from './layouts/FullScreenLayout';
import RootLayout from './layouts/RootLayout';
import BrandLoading from '@/components/organisms/BrandLoading';
import { SCREENS } from './screens';

const ActualContentPage = lazy(() => import('@/pages/ActualContent'));
const ManualItineraryPage = lazy(() => import('@/pages/ManualItinerary'));
const SelectedItineraryPage = lazy(() => import('@/pages/SelectedItinerary'));
const ItineraryEntry = lazy(() => import('@/pages/ActualItinerary'));
const ActualTravelInfoPage = lazy(() => import('@/pages/ActualTravelInfo'));
const CommerceRefundPage = lazy(() => import('@/pages/CommerceRefunds'));
const RouteError = lazy(() => import('./RouteError'));

// 화면은 라우트 단위로 코드 스플리팅한다 (초기 JS < 250KB gzip, 실행계획 6.9).
const WelcomePage = lazy(() => import('@/pages/Welcome'));
const HomePage = firstMvp
  ? lazy(() => import('@/pages/MvpHome'))
  : lazy(() => import('@/pages/Home'));
const SearchPage = lazy(() => import('@/pages/Search'));
const ActualSearchPage = lazy(() => import('@/pages/ActualSearch'));
const DiagnosisPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/mocks/pages/DemoDiagnosis'))
    : lazy(() => import('@/pages/Diagnosis'));
const DiagnosisResultPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/mocks/pages/DemoDiagnosisResult'))
    : lazy(() => import('@/pages/DiagnosisResult'));
const AuthPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/mocks/pages/DemoAuth'))
    : lazy(() => import('@/pages/Auth'));
const ExplorePage = firstMvp
  ? lazy(() => import('@/pages/ActualExplore'))
  : lazy(() => import('@/pages/Explore'));
const ExploreMapPage = lazy(() => import('@/pages/ExploreMap'));
const ExperienceDetailPage = lazy(() => import('@/pages/ExperienceDetail'));
const TreatmentDetailPage = lazy(() => import('@/pages/TreatmentDetail'));
const ClinicDetailPage = lazy(() => import('@/pages/ClinicDetail'));
const ActualCatalogDetailPage = lazy(
  () => import('@/pages/ActualCatalogDetail'),
);
const ActualExploreMapPage = lazy(() => import('@/pages/ActualExploreMap'));
const QuestionsPage = lazy(() => import('@/pages/Questions'));
const QuestionDetailPage = lazy(() => import('@/pages/QuestionDetail'));
const JourneysPage = lazy(() => import('@/pages/Journeys'));
const JourneyDetailPage = lazy(() => import('@/pages/JourneyDetail'));
const TrendsPage = lazy(() => import('@/pages/Trends'));
const TrendDetailPage = lazy(() => import('@/pages/TrendDetail'));
const ChatPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/mocks/pages/DemoChat'))
    : lazy(() => import('@/pages/Chat'));
const ChatRoomPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/mocks/pages/DemoChatRoom'))
    : lazy(() => import('@/pages/ChatRoom'));
const GuestChatRoomPage = lazy(() => import('@/pages/GuestChat/Room'));
const PlannerRoomsPage = lazy(() => import('@/pages/PlannerRooms'));
const PlansPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/mocks/pages/DemoPlans'))
    : lazy(() => import('@/pages/Plans'));
const CheckoutPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/pages/Checkout'))
    : lazy(() => import('@/pages/ManualCheckout'));
const CheckoutCompletePage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/pages/CheckoutComplete'))
    : lazy(() => import('@/pages/ManualCheckout'));
const TripPage = lazy(() => import('@/pages/Trip'));
const ItineraryPage = lazy(() => import('@/pages/Itinerary'));
const PlannerPage = lazy(() => import('@/pages/Planner'));
const BookingsPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/pages/Bookings'))
    : lazy(() => import('@/pages/ManualBookings'));
const BookingDetailPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/pages/BookingDetail'))
    : lazy(() => import('@/pages/ManualBookings'));
const TravelInfoPage = lazy(() => import('@/pages/TravelInfo'));
const SavedPage = firstMvp
  ? lazy(() => import('@/pages/ActualSaved'))
  : lazy(() => import('@/pages/Saved'));
const RecentPage = firstMvp
  ? lazy(() => import('@/pages/ActualRecent'))
  : lazy(() => import('@/pages/Recent'));
const AftercareListPage = lazy(() => import('@/pages/AftercareList'));
const AftercarePage = lazy(() => import('@/pages/Aftercare'));
const MyPage = firstMvp
  ? lazy(() => import('@/pages/MvpAccount'))
  : lazy(() => import('@/pages/My'));
const SettingsPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/pages/Settings'))
    : lazy(() => import('@/pages/MvpConsents'));
const MembershipPage = firstMvp
  ? lazy(() => import('@/pages/ActualMembership'))
  : lazy(() => import('@/pages/Membership'));
const PointsPage = firstMvp
  ? lazy(() => import('@/pages/ActualPoints'))
  : lazy(() => import('@/pages/Points'));
const ProductsPage = lazy(() => import('@/pages/Products'));
const VouchersPage = lazy(() => import('@/pages/ActualVouchers'));
const CouponsPage = firstMvp
  ? lazy(() => import('@/pages/ActualCoupons'))
  : lazy(() => import('@/pages/Coupons'));
const ReviewsPage = lazy(() => import('@/pages/Reviews'));
const PaymentsPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/pages/Payments'))
    : lazy(() => import('@/pages/ManualPayments'));
const BookingReviewPage = lazy(() => import('@/pages/BookingReview'));
const NotificationsPage = lazy(() => import('@/pages/Notifications'));
const HelpPage = firstMvp
  ? lazy(() => import('@/pages/ActualHelp'))
  : lazy(() => import('@/pages/Help'));
const LegalPage = firstMvp
  ? lazy(() => import('@/pages/ActualLegal'))
  : lazy(() => import('@/pages/Legal'));
const NotFoundPage = lazy(() => import('@/pages/NotFound'));
const ComponentsPreviewPage =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/mocks/pages/ComponentsPreview'))
    : undefined;
const SystemPreviewPage =
  import.meta.env.VITE_ENABLE_MSW === 'true'
    ? lazy(() => import('@/mocks/pages/SystemPreview'))
    : undefined;

/**
 * 고객 웹 라우트 (실행계획 6.4).
 * - RootLayout: 집중 플로우를 제외한 모든 화면에서 공통 하단 탭 유지
 * - AppShell / FullScreenLayout: 기본 헤더 / 뒤로 가기 헤더와 본문
 */
export const router = createBrowserRouter(
  productRoutes([
    {
      element: <RootLayout />,
      errorElement: (
        <Suspense fallback={<BrandLoading />}>
          <RouteError />
        </Suspense>
      ),
      children: [
        {
          element: <AppShell />,
          children: [
            {
              path: SCREENS.home.path,
              handle: { screen: 'home' },
              element: <HomePage />,
            },
            {
              path: SCREENS.explore.path,
              handle: { screen: 'explore' },
              element: <ExplorePage />,
            },
            {
              path: SCREENS.questions.path,
              handle: { screen: 'questions' },
              element: firstMvp ? (
                <ActualContentPage kind="QUESTION" />
              ) : (
                <QuestionsPage />
              ),
            },
            {
              path: SCREENS.journeys.path,
              handle: { screen: 'journeys' },
              element: firstMvp ? (
                <ActualContentPage kind="STORY" />
              ) : (
                <JourneysPage />
              ),
            },
            {
              path: SCREENS.trends.path,
              handle: { screen: 'trends' },
              element: firstMvp ? (
                <ActualContentPage kind="TREND" />
              ) : (
                <TrendsPage />
              ),
            },
            {
              path: SCREENS.chat.path,
              handle: { screen: 'chat' },
              element: <ChatPage />,
            },
            {
              path: SCREENS.trip.path,
              handle: { screen: 'trip' },
              element: (
                <RequireMember>
                  <TripPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.bookings.path,
              handle: { screen: 'bookings' },
              element: (
                <RequireMember>
                  <BookingsPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.saved.path,
              handle: { screen: 'saved' },
              element: (
                <RequireMember>
                  <SavedPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.recent.path,
              handle: { screen: 'recent' },
              element: <RecentPage />,
            },
            {
              path: SCREENS.my.path,
              handle: { screen: 'my' },
              element: <MyPage />,
            },
          ],
        },
        {
          element: <FullScreenLayout />,
          children: [
            {
              path: SCREENS.welcome.path,
              handle: { screen: 'welcome' },
              element: <WelcomePage />,
            },
            {
              path: SCREENS.search.path,
              handle: { screen: 'search' },
              element: firstMvp ? <ActualSearchPage /> : <SearchPage />,
            },
            {
              path: SCREENS.diagnosis.path,
              handle: { screen: 'diagnosis' },
              element: <DiagnosisPage />,
            },
            {
              path: SCREENS.diagnosisResult.path,
              handle: { screen: 'diagnosisResult' },
              element: <DiagnosisResultPage />,
            },
            {
              path: SCREENS.auth.path,
              handle: { screen: 'auth' },
              element: <AuthPage />,
            },
            {
              path: SCREENS.exploreMap.path,
              handle: { screen: 'exploreMap' },
              element: firstMvp ? <ActualExploreMapPage /> : <ExploreMapPage />,
            },
            {
              path: SCREENS.experienceDetail.path,
              handle: { screen: 'experienceDetail' },
              element: firstMvp ? (
                <ActualCatalogDetailPage kind="EXPERIENCE" />
              ) : (
                <ExperienceDetailPage />
              ),
            },
            {
              path: SCREENS.treatmentDetail.path,
              handle: { screen: 'treatmentDetail' },
              element: firstMvp ? (
                <ActualCatalogDetailPage kind="TREATMENT" />
              ) : (
                <TreatmentDetailPage />
              ),
            },
            {
              path: SCREENS.clinicDetail.path,
              handle: { screen: 'clinicDetail' },
              element: firstMvp ? (
                <ActualCatalogDetailPage kind="CLINIC" />
              ) : (
                <ClinicDetailPage />
              ),
            },
            {
              path: SCREENS.questionDetail.path,
              handle: { screen: 'questionDetail' },
              element: firstMvp ? (
                <ActualContentPage kind="QUESTION" detail />
              ) : (
                <QuestionDetailPage />
              ),
            },
            {
              path: SCREENS.journeyDetail.path,
              handle: { screen: 'journeyDetail' },
              element: firstMvp ? (
                <ActualContentPage kind="STORY" detail />
              ) : (
                <JourneyDetailPage />
              ),
            },
            {
              path: SCREENS.trendDetail.path,
              handle: { screen: 'trendDetail' },
              element: firstMvp ? (
                <ActualContentPage kind="TREND" detail />
              ) : (
                <TrendDetailPage />
              ),
            },
            {
              path: SCREENS.plannerInbox.path,
              handle: { screen: 'plannerInbox' },
              element: <PlannerRoomsPage />,
            },
            {
              path: SCREENS.plannerConversation.path,
              handle: { screen: 'plannerConversation' },
              element: <PlannerRoomsPage />,
            },
            {
              path: '/chat/guest/:roomId',
              handle: { screen: 'chatRoom' },
              element: <GuestChatRoomPage />,
            },
            {
              path: SCREENS.chatRoom.path,
              handle: { screen: 'chatRoom' },
              element: (
                <RequireMember>
                  <ChatRoomPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.plans.path,
              handle: { screen: 'plans' },
              element: (
                <RequireMember>
                  <PlansPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.products.path,
              handle: { screen: 'products' },
              element: <ProductsPage />,
            },
            {
              path: SCREENS.productDetail.path,
              handle: { screen: 'productDetail' },
              element: <ProductsPage />,
            },
            {
              path: SCREENS.checkout.path,
              handle: { screen: 'checkout' },
              element: (
                <RequireMember>
                  <CheckoutPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.checkoutComplete.path,
              handle: { screen: 'checkoutComplete' },
              element: (
                <RequireMember>
                  <CheckoutCompletePage />
                </RequireMember>
              ),
            },
            {
              path: '/chat/:roomId/itinerary',
              handle: { screen: 'itinerary' },
              element: (
                <RequireMember>
                  <ManualItineraryPage />
                </RequireMember>
              ),
            },
            {
              path: '/trip/itinerary',
              handle: { screen: 'itinerary' },
              element: (
                <RequireMember>
                  <ItineraryEntry />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.itinerary.path,
              handle: { screen: 'itinerary' },
              element: (
                <RequireMember>
                  {firstMvp ? <SelectedItineraryPage /> : <ItineraryPage />}
                </RequireMember>
              ),
            },
            {
              path: SCREENS.planner.path,
              handle: { screen: 'planner' },
              element: <PlannerPage />,
            },
            {
              path: SCREENS.bookingDetail.path,
              handle: { screen: 'bookingDetail' },
              element: (
                <RequireMember>
                  <BookingDetailPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.travelInfo.path,
              handle: { screen: 'travelInfo' },
              element: (
                <RequireMember>
                  {firstMvp ? <ActualTravelInfoPage /> : <TravelInfoPage />}
                </RequireMember>
              ),
            },
            {
              path: SCREENS.aftercareList.path,
              handle: { screen: 'aftercareList' },
              element: (
                <RequireMember>
                  <AftercareListPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.aftercare.path,
              handle: { screen: 'aftercare' },
              element: (
                <RequireMember>
                  <AftercarePage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.settings.path,
              handle: { screen: 'settings' },
              element: (
                <RequireMember>
                  <SettingsPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.membership.path,
              handle: { screen: 'membership' },
              element: <MembershipPage />,
            },
            {
              path: SCREENS.points.path,
              handle: { screen: 'points' },
              element: (
                <RequireMember>
                  <PointsPage />
                </RequireMember>
              ),
            },
            ...(['vouchers', 'voucherDetail', 'voucherOrder'] as const).map(
              (screen) => ({
                path: SCREENS[screen].path,
                handle: { screen },
                element: (
                  <RequireMember>
                    <VouchersPage />
                  </RequireMember>
                ),
              }),
            ),
            {
              path: SCREENS.coupons.path,
              handle: { screen: 'coupons' },
              element: (
                <RequireMember>
                  <CouponsPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.reviews.path,
              handle: { screen: 'reviews' },
              element: (
                <RequireMember>
                  <ReviewsPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.commerceRefund.path,
              handle: { screen: 'commerceRefund' },
              element: (
                <RequireMember>
                  <CommerceRefundPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.payments.path,
              handle: { screen: 'payments' },
              element: (
                <RequireMember>
                  <PaymentsPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.bookingReview.path,
              handle: { screen: 'bookingReview' },
              element: (
                <RequireMember>
                  <BookingReviewPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.notifications.path,
              handle: { screen: 'notifications' },
              element: (
                <RequireMember>
                  <NotificationsPage />
                </RequireMember>
              ),
            },
            {
              path: SCREENS.help.path,
              handle: { screen: 'help' },
              element: <HelpPage />,
            },
            {
              path: SCREENS.legal.path,
              handle: { screen: 'legal' },
              element: <LegalPage />,
            },
            {
              path: SCREENS.notFound.path,
              handle: { screen: 'notFound' },
              element: <NotFoundPage />,
            },
            ...(ComponentsPreviewPage
              ? [
                  {
                    path: '/__preview/components',
                    element: <ComponentsPreviewPage />,
                  },
                ]
              : []),
            ...(SystemPreviewPage
              ? [
                  {
                    path: '/__preview/system/error',
                    element: <SystemPreviewPage />,
                  },
                ]
              : []),
          ],
        },
      ],
    },
  ]),
);
